import { useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { SecretCreateForm, type CreateSecretTier } from './SecretCreateForm';

export interface AddSecretPopoverProps {
  /**
   * Tier options the form should render. Omit for surfaces that already
   * know the tier (e.g. the admin page only writes org-tier).
   */
  tierOptions?: CreateSecretTier[];
  /** Id of the initially-selected tier; defaults to `tierOptions[0]`. */
  defaultTierId?: string;
  /**
   * Fired on form submit. Caller owns the mutation; the popover closes
   * synchronously so the caller's toast (success or error) lands on the
   * same surface the user invoked the action from.
   */
  onSubmit: (name: string, value: string, tier: CreateSecretTier | undefined) => void;
  /** Drives the form's Save button label / disabled state. */
  isPending?: boolean;
  /** Pre-fill the name field — useful when opened from a search context. */
  initialName?: string;
  /** Overrides the default trigger button. */
  trigger?: ReactNode;
  /** Label for the default trigger button. */
  triggerLabel?: string;
  /** Disables the trigger (e.g. while a delete mutation is in flight). */
  disabled?: boolean;
  className?: string;
}

/**
 * Compact "Add secret" popover. Wraps {@link SecretCreateForm} in a
 * Radix Popover so a user can add a custom secret without leaving the
 * page — replaces the heavier dialog UX that every secrets surface used
 * to mount on its own.
 *
 * The popover owns its own open/close lifecycle and closes the moment
 * `onSubmit` fires; the caller's mutation result surfaces via toast.
 * Mounting the form only while `open` is true keeps each re-open
 * starting from a clean state.
 */
export function AddSecretPopover({
  tierOptions,
  defaultTierId,
  onSubmit,
  isPending = false,
  initialName,
  trigger,
  triggerLabel = 'Add secret',
  disabled,
  className,
}: AddSecretPopoverProps) {
  const [open, setOpen] = useState(false);

  function handleSubmit(name: string, value: string, tier: CreateSecretTier | undefined) {
    onSubmit(name, value, tier);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button size="sm" disabled={disabled} data-testid="add-secret-popover-trigger">
            <Plus className="size-3.5" />
            {triggerLabel}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={className ?? 'w-80 p-3'}
        data-testid="add-secret-popover-content"
      >
        {open ? (
          <>
            <p className="mb-3 text-xs font-medium">Add custom secret</p>
            <SecretCreateForm
              initialName={initialName}
              isPending={isPending}
              tierOptions={tierOptions}
              defaultTierId={defaultTierId}
              onSubmit={handleSubmit}
              onCancel={() => setOpen(false)}
            />
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
